import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getDataImportStatusCounts } from "../../services/dataImportApi";

// Statusy importu wymagajace uwagi uzytkownika (HistoryBulkStatus)
const STATUS_TO_ACCEPT = -1; // Do akceptacji
const STATUS_ERROR = 4;      // Przerwany - blad
const STATUS_ROLLBACK_ERROR = 9; // Wycofanie nieudane

// Odczyt pojedynczego licznika ze slownika { "-1": 6, "4": 3, ... }.
// Backend zwraca tylko statusy faktycznie wystepujace w bazie — brak klucza oznacza 0.
const readCount = (counts, status) => {
    const value = counts?.[String(status)];
    return Number.isFinite(value) ? value : 0;
};

const DataImportCard = ({ isSuperUser = false }) => {
    const [toAcceptCount, setToAcceptCount] = useState(0);
    const [problemCount, setProblemCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Zwykly uzytkownik widzi importy swojego oddzialu, ale ich nie akceptuje —
    // etykieta "do akceptacji" sugerowalaby akcje, ktorej nie moze wykonac.
    const subtitle = isSuperUser
        ? "Importy oczekujące na akceptację oraz zakończone błędem"
        : "Importy Twojego oddziału oczekujące na akceptację oraz zakończone błędem";

    const pendingLabel = isSuperUser ? "do akceptacji" : "oczekuje";

    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;

        (async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Jedno zapytanie GROUP BY zwraca komplet licznikow
                const response = await getDataImportStatusCounts(controller.signal);

                if (cancelled) return;

                const counts = response?.data;

                setToAcceptCount(readCount(counts, STATUS_TO_ACCEPT));
                setProblemCount(
                    readCount(counts, STATUS_ERROR) + readCount(counts, STATUS_ROLLBACK_ERROR)
                );
            } catch (e) {
                if (cancelled) return;
                if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
                setError(e);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, []);

    const statusBadge = useMemo(() => {
        if (isLoading) {
            return (
                <Badge bg="light" text="dark" className="waiting-changes-card__badge">
                    Ładowanie…
                </Badge>
            );
        }

        if (error) {
            return (
                <Badge bg="danger" className="waiting-changes-card__badge">
                    Błąd API
                </Badge>
            );
        }

        // Bledy maja wyzszy priorytet wizualny niz zwykla kolejka do akceptacji
        if (problemCount > 0) {
            return (
                <Badge bg="danger" className="waiting-changes-card__badge">
                    Błędy importu
                </Badge>
            );
        }

        if (toAcceptCount > 0) {
            return (
                <Badge bg="warning" text="dark" className="waiting-changes-card__badge">
                    {isSuperUser ? "Do akceptacji" : "Oczekujące"}
                </Badge>
            );
        }

        return (
            <Badge bg="success" className="waiting-changes-card__badge">
                Bez zaległości
            </Badge>
        );
    }, [error, isLoading, isSuperUser, problemCount, toAcceptCount]);

    return (
        <Card className="waiting-changes-card border-0 shadow-sm">
            <Card.Body className="d-flex flex-column justify-content-between">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                        <Card.Title as="h5" className="waiting-changes-card__title mb-1">
                            Import danych
                        </Card.Title>
                        <Card.Text className="waiting-changes-card__subtitle mb-0">
                            {subtitle}
                        </Card.Text>
                    </div>
                    {statusBadge}
                </div>

                <div className="waiting-changes-card__metric-wrap mt-2 mb-2">
                    {isLoading ? (
                        <div className="d-flex align-items-center gap-2 text-muted">
                            <Spinner size="sm" animation="border" />
                            <span>Pobieranie danych…</span>
                        </div>
                    ) : (
                        <div className="d-flex gap-4">
                            <div className="waiting-changes-card__metric">
                                <span className="waiting-changes-card__metric-number">
                                    {toAcceptCount}
                                </span>
                                <span className="waiting-changes-card__metric-label">
                                    {pendingLabel}
                                </span>
                            </div>
                            <div className="waiting-changes-card__metric">
                                <span
                                    className="waiting-changes-card__metric-number"
                                    // Wyroznienie tylko gdy faktycznie sa bledy — zero nie powinno alarmowac
                                    style={problemCount > 0 ? { color: "#d40511" } : undefined}
                                >
                                    {problemCount}
                                </span>
                                <span className="waiting-changes-card__metric-label">
                                    błędów
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-end">
                    <Button
                        as={Link}
                        to="/data-import"
                        variant="dark"
                        size="sm"
                        className="waiting-changes-card__cta"
                    >
                        Otwórz listę
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default DataImportCard;
